import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@/lib/utils';

const AUTOPLAY_INTERVAL_MS = 3000;
const BANNER_HEIGHT_PX = 160;

/**
 * @typedef {Object} Banner
 * @property {string} id
 * @property {string} [imageUrl]
 * @property {string} [alt]       - Descriptive alt text for the image (falls back to title)
 * @property {string} [gradient]  - Tailwind gradient classes used as fallback when imageUrl is absent
 * @property {string} title
 * @property {string} [subtitle]
 * @property {string} [ctaLabel]
 * @property {string} [targetUrl]
 */

/**
 * Static mock banners — replace with backend data by passing a `banners` prop.
 * Expected shape matches the Banner typedef above.
 *
 * @type {Banner[]}
 */
export const MOCK_BANNERS = [
  {
    id: '1',
    imageUrl:
      'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=800&h=320&q=80',
    alt: 'Taza de café de especialidad con latte art en una cafetería premium',
    title: '2x1 en café de especialidad',
    subtitle: 'Todos los lunes y miércoles, sin límite',
    ctaLabel: 'Ver más',
  },
  {
    id: '2',
    imageUrl:
      'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?auto=format&fit=crop&w=800&h=320&q=80',
    alt: 'Desayuno premium con avocado toast, huevos pochados y jugo fresco',
    title: 'Desayuno premium',
    subtitle: 'Arrancá el día con energía — incluye bebida',
    ctaLabel: 'Ver menú',
  },
  {
    id: '3',
    imageUrl:
      'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=800&h=320&q=80',
    alt: 'Bowl gourmet con ensalada fresca, granos y proteínas para almuerzo saludable',
    title: '20% OFF en almuerzos',
    subtitle: 'Menú del día con bebida incluida, de lunes a viernes',
    ctaLabel: 'Ver opciones',
  },
  {
    id: '4',
    imageUrl:
      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&h=320&q=80',
    alt: 'Presentación elegante de plato de alta cocina en restaurante premium',
    title: 'Miembros exclusivos',
    subtitle: 'Puntos triplicados por 48 horas — solo para vos',
  },
];

/**
 * Promotional banner carousel for the customer home screen.
 * Renders nothing when the banners array is empty.
 *
 * Usage with backend data:
 *   <CustomerBannerCarousel banners={bannersFromApi} />
 *
 * @param {{ banners?: Banner[] }} props
 */
export default function CustomerBannerCarousel({ banners = MOCK_BANNERS }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'center' });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const autoplayTimerRef = useRef(null);

  const stopAutoplay = useCallback(() => {
    if (autoplayTimerRef.current !== null) {
      clearInterval(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    stopAutoplay();
    autoplayTimerRef.current = setInterval(() => {
      emblaApi?.scrollNext();
    }, AUTOPLAY_INTERVAL_MS);
  }, [emblaApi, stopAutoplay]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  // Wire embla events and start autoplay once the API is ready.
  useEffect(() => {
    if (!emblaApi || !banners.length) return;

    emblaApi.on('select', onSelect);
    emblaApi.on('pointerDown', stopAutoplay);
    emblaApi.on('pointerUp', startAutoplay);
    onSelect();
    startAutoplay();

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('pointerDown', stopAutoplay);
      emblaApi.off('pointerUp', startAutoplay);
      stopAutoplay();
    };
  }, [emblaApi, banners.length, onSelect, startAutoplay, stopAutoplay]);

  // Guarantee timer is cleared on unmount regardless of emblaApi state.
  useEffect(() => {
    return () => stopAutoplay();
  }, [stopAutoplay]);

  if (!banners.length) return null;

  const handleBannerClick = (banner) => {
    if (banner.targetUrl) {
      window.open(banner.targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="px-4 pt-4 pb-1">
      {/* Viewport — clips slides and applies rounded corners */}
      <div ref={emblaRef} className="overflow-hidden rounded-2xl">
        <div className="flex">
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              className="min-w-0 shrink-0 grow-0 basis-full"
              onClick={() => handleBannerClick(banner)}
              style={{ cursor: banner.targetUrl ? 'pointer' : 'default' }}
            >
              <div
                className={cn(
                  'relative w-full overflow-hidden',
                  !banner.imageUrl && banner.gradient
                    ? `bg-gradient-to-br ${banner.gradient}`
                    : 'bg-slate-900'
                )}
                style={{ height: BANNER_HEIGHT_PX }}
              >
                {banner.imageUrl && (
                  <img
                    src={banner.imageUrl}
                    alt={banner.alt || banner.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                )}

                {/* Scrim ensures text readability over any image */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />

                <div className="absolute bottom-0 left-0 right-0 px-4 pb-5 pt-3">
                  <p className="font-outfit font-bold text-white text-base leading-snug">
                    {banner.title}
                  </p>
                  {banner.subtitle && (
                    <p className="text-white/70 text-xs mt-0.5 leading-snug">
                      {banner.subtitle}
                    </p>
                  )}
                  {banner.ctaLabel && (
                    <span className="mt-2 inline-block text-xs font-semibold text-white bg-white/20 rounded-full px-3 py-1 backdrop-blur-sm border border-white/10">
                      {banner.ctaLabel} →
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center items-center gap-1.5 mt-2.5">
        {banners.map((banner, i) => (
          <button
            key={banner.id}
            onClick={() => {
              stopAutoplay();
              emblaApi?.scrollTo(i);
              startAutoplay();
            }}
            className={cn(
              'rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
              i === selectedIndex
                ? 'w-5 h-1.5 bg-white'
                : 'w-1.5 h-1.5 bg-white/25 hover:bg-white/50'
            )}
            aria-label={`Ver banner: ${banner.title}`}
            aria-current={i === selectedIndex ? 'true' : undefined}
          />
        ))}
      </div>
    </div>
  );
}
